import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listUsers,
  getUser,
  updateSubscriptionOverrides,
  setUserDisabled,
  getBillingEvents,
  getStats,
  listPlans,
  getPlan,
  updatePlan,
  getAuditLogs,
  adminSearch,
  giftScans,
  bulkGiftScans,
  getGiftHistory,
  createPlan,
  replayBillingEvent,
  deleteUserAccount,
} from "../api/admin.js";
import {
  listOrgs,
  getOrg,
  listWebsites,
  getWebsite,
  listRuns,
  listDocuments,
  listStatements,
  listRecurring,
  getSupportAlerts,
} from "../api/admin-summaries.js";
import { getStorageUsage } from "../api/admin-storage.js";

export function useAdminUsers(params?: {
  page?: number;
  limit?: number;
  search?: string;
}) {
  return useQuery({
    queryKey: ["admin-users", params],
    queryFn: () => listUsers(params),
  });
}

export function useAdminUser(userId: string | undefined) {
  return useQuery({
    queryKey: ["admin-user", userId],
    queryFn: () => {
      if (!userId) throw new Error("userId is required");
      return getUser(userId);
    },
    enabled: userId != null,
  });
}

export function useUpdateSubscriptionOverrides(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      overrides: Parameters<typeof updateSubscriptionOverrides>[1],
    ) => updateSubscriptionOverrides(userId, overrides),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-user", userId] });
    },
  });
}

export function useSetUserDisabled(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (disabled: boolean) => setUserDisabled(userId, disabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}

export function useAdminBillingEvents(params?: {
  page?: number;
  limit?: number;
  type?: string;
  fromDate?: string;
}) {
  return useQuery({
    queryKey: ["admin-billing-events", params],
    queryFn: () => getBillingEvents(params),
  });
}

export function useAdminStats() {
  return useQuery({
    queryKey: ["admin-stats"],
    queryFn: getStats,
    staleTime: 60 * 1000, // 1 minute
  });
}

// ── Plan hooks ─────────────────────────────────────────────────────────────

export function useAdminPlans() {
  return useQuery({
    queryKey: ["admin-plans"],
    queryFn: listPlans,
  });
}

export function useAdminPlan(planId: string | undefined) {
  return useQuery({
    queryKey: ["admin-plan", planId],
    queryFn: () => {
      if (!planId) throw new Error("planId is required");
      return getPlan(planId);
    },
    enabled: planId != null,
  });
}

export function useUpdatePlan(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (updates: Parameters<typeof updatePlan>[1]) =>
      updatePlan(planId, updates),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-plans"] });
      void qc.invalidateQueries({ queryKey: ["admin-plan", planId] });
    },
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (plan: Parameters<typeof createPlan>[0]) => createPlan(plan),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-plans"] });
    },
  });
}

// ── Summary hooks ──────────────────────────────────────────────────────────

export function useAdminOrgs(params?: {
  page?: number;
  limit?: number;
  search?: string;
}) {
  return useQuery({
    queryKey: ["admin-orgs", params],
    queryFn: () => listOrgs(params),
  });
}

export function useAdminOrg(orgId: string | undefined) {
  return useQuery({
    queryKey: ["admin-org", orgId],
    queryFn: () => {
      if (!orgId) throw new Error("orgId is required");
      return getOrg(orgId);
    },
    enabled: orgId != null,
  });
}

export function useAdminWebsites(params?: {
  page?: number;
  limit?: number;
  search?: string;
}) {
  return useQuery({
    queryKey: ["admin-websites", params],
    queryFn: () => listWebsites(params),
  });
}

export function useAdminWebsite(websiteId: string | undefined) {
  return useQuery({
    queryKey: ["admin-website", websiteId],
    queryFn: () => {
      if (!websiteId) throw new Error("websiteId is required");
      return getWebsite(websiteId);
    },
    enabled: websiteId != null,
  });
}

export function useAdminRuns(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ["admin-runs", params],
    queryFn: () => listRuns(params),
  });
}

export function useAdminDocuments(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ["admin-documents", params],
    queryFn: () => listDocuments(params),
  });
}

export function useAdminStatements(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ["admin-statements", params],
    queryFn: () => listStatements(params),
  });
}

export function useAdminRecurring(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ["admin-recurring", params],
    queryFn: () => listRecurring(params),
  });
}

export function useAdminSupport() {
  return useQuery({
    queryKey: ["admin-support"],
    queryFn: getSupportAlerts,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useAdminStorageUsage() {
  return useQuery({
    queryKey: ["admin-storage-usage"],
    queryFn: getStorageUsage,
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useAdminAuditLogs(params?: {
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["admin-audit-logs", params],
    queryFn: () => getAuditLogs(params),
  });
}

export function useAdminSearch(q: string) {
  return useQuery({
    queryKey: ["admin-search", q],
    queryFn: () => adminSearch(q),
    enabled: q.length >= 2,
    staleTime: 10 * 1000,
  });
}

export function useGiftScans(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { amount: number; reason: string; websiteId?: string | null }) =>
      giftScans(orgId, params),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-gift-history", orgId] });
      void qc.invalidateQueries({ queryKey: ["admin-user"] });
    },
  });
}

export function useBulkGiftScans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { userIds: string[]; amount: number; reason: string }) =>
      bulkGiftScans(params),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}

export function useGiftHistory(
  orgId: string | undefined,
  params?: { page?: number; limit?: number },
) {
  return useQuery({
    queryKey: ["admin-gift-history", orgId, params],
    queryFn: () => {
      if (!orgId) throw new Error("orgId is required");
      return getGiftHistory(orgId, params);
    },
    enabled: orgId != null,
  });
}

export function useReplayBillingEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => replayBillingEvent(eventId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-billing-events"] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => deleteUserAccount(userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
}
